import { useLocation } from 'react-router'
import cn from 'clsx'

// Components
import { Link, IconTracklibLogoSymbol, Section } from '@tracklib/kit'
import SocialIcons from 'components/socialicons'

// Utils
import buildInfo from 'version'

import styles from './footer.css'

type NavLinkProps = {
  text: string
  url: string
  openInNewTab?: boolean
}

const NavLink = ({ text, url, openInNewTab }: NavLinkProps): JSX.Element | null => {
  if (openInNewTab) {
    return (
      <li key={url}>
        <a href={url} target="_blank" rel="noopener noreferrer">
          {text}
        </a>
      </li>
    )
  }

  return (
    <li key={url}>
      <Link to={url}>{text}</Link>
    </li>
  )
}

const Footer = (): JSX.Element | null => {
  const { pathname } = useLocation()
  //  pathname: state.router.location.pathname,
  // isAuthenticated: state.auth.isAuthenticated,
  const noFooter =
    pathname.startsWith('/tracks') ||
    pathname.startsWith('/collection/') ||
    pathname.startsWith('/checkout') ||
    pathname.startsWith('/pay') ||
    pathname.startsWith('/license') ||
    pathname.startsWith('/artist/') ||
    pathname.startsWith('/genre/') ||
    pathname.startsWith('/label/') ||
    pathname.startsWith('/signup/') ||
    pathname.startsWith('/purchasedtracks') ||
    pathname.startsWith('/favorites') ||
    pathname.startsWith('/subscribe') ||
    pathname.startsWith('/onboard') ||
    pathname.startsWith('/interestform')

  if (noFooter) {
    return null
  }

  const menus: Array<{ title: string; items: Array<{ name: string; url: string; external?: boolean }> }> = [
    {
      title: 'Music',
      items: [
        { name: 'Inspiration', url: '/inspiration/' },
        { name: 'All tracks', url: '/tracks/' },
        { name: 'Collections', url: '/collections/' },
        { name: 'Genres', url: '/genres/' },
        { name: 'Artists', url: '/artists/' },
        { name: 'Labels', url: '/labels/' },
      ],
    },
    {
      title: 'How it works',
      items: [
        { name: 'Plans & Pricing', url: '/plans/' },
        { name: 'How to use', url: '/howtouse/' },
        { name: 'Licensing', url: '/licensing/' },
        { name: 'Our catalog', url: '/music/' },
      ],
    },
    {
      title: 'About',
      items: [
        { name: 'About Us', url: '/about/' },
        { name: 'Testimonials', url: '/testimonials/' },
        { name: 'Contact Us', url: '/contact/' },
        { name: 'Press', url: '/press/' },
        { name: 'Careers', url: 'https://careers.tracklib.com/', external: true },
      ],
    },
    {
      title: 'Support',
      items: [
        {
          name: 'Support',
          url: 'https://support.tracklib.com/',
          external: true,
        },
        {
          name: 'Your Account',
          url: 'https://support.tracklib.com/hc/en-us/sections/360003946340-Your-account',
          external: true,
        },
        {
          name: 'Subscription & Downloads',
          url: 'https://support.tracklib.com/hc/en-us/sections/360003987379-Subscription-downloads',
          external: true,
        },
        {
          name: 'Licensing',
          url: 'https://support.tracklib.com/hc/en-us/categories/360002492820-Licensing',
          external: true,
        },
      ],
    },
    {
      title: 'Legal',
      items: [
        { name: 'Terms of Website Use', url: '/terms/' },
        { name: 'Privacy Policy', url: '/privacy/' },
        { name: 'Cookies Policy', url: '/cookies/' },
      ],
    },
  ]

  return (
    <footer className={cn(styles.wrapper, pathname === '/' && styles.dark)}>
      <Section marginBottom={0} extraPad>
        <div className={styles.menus}>
          {menus.map(({ title, items }) => (
            <nav className={styles.nav} key={title}>
              <p>{title}</p>
              <ul>
                {items.map(({ name, url, external = false }) => (
                  <NavLink text={name} url={url} key={`${name}-${url}`} openInNewTab={external} />
                ))}
              </ul>
            </nav>
          ))}
        </div>

        <div className={styles.heel}>
          <div className={styles.logoSection}>
            <IconTracklibLogoSymbol className={styles.logo} />
            <p className={styles.copy}>
              &copy; {new Date().getFullYear()} Tracklib. All rights reserved. {buildInfo.tag}
            </p>
          </div>

          <SocialIcons className={styles.socialIcons} />
        </div>
      </Section>
    </footer>
  )
}

export default Footer
